import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { EnrollmentService } from '../../services/enrollment.service';
import { EnrollmentDraft, EnrollmentLandingPageInfo, EnrollmentType, ErrorDto, CreateEnrollmentDraftResponse, CompleteEnrollmentRequest, MasterClientProductViewDto, CheckDraftStatusResponse } from '../../models/api-data-contracts';
import { ActivatedRoute, Router } from '@angular/router';
import { LandingPagesService } from '../../services/landing-pages.service';
import { MemberFormComponent } from './member-form/member-form.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VerifyIdentityComponent } from "./verify-identity/verify-identity.component";
import { ConfirmationComponent } from "./confirmation/confirmation.component";
import { CheckoutComponent } from "./checkout/checkout.component";
import { EnrollmentClosedComponent } from "./enrollment-closed/enrollment-closed.component";
import { BillingTypes, FormErrors, ResponseErrors } from '../../common/enums';
import { TurnstileTokenService } from '../../services/turnstile-token.service';
import { EnrollmentLandingPathRules } from '../../common/enrollment-landing-path.rules';
import { remapSelectionForDate } from '../../common/helpers';

type step = 'data' | 'verify' | 'payment' | 'confirmation' | 'closed';

@Component({
  selector: 'app-enrollment',
  imports: [CommonModule, MemberFormComponent, MatProgressSpinnerModule, VerifyIdentityComponent, ConfirmationComponent, CheckoutComponent, EnrollmentClosedComponent],
  templateUrl: './enrollment.component.html',
  styleUrl: './enrollment.component.scss'
})
export class EnrollmentComponent implements OnInit {
  loading: boolean = false;
  clientInfo: EnrollmentLandingPageInfo;
  memberData: EnrollmentDraft;
  draftResponse: CreateEnrollmentDraftResponse;
  validationToken: string;
  sessionToken: string;
  maskedEmail: string;
  otpExpiresAt: string | null = null;
  isEmailVerified: boolean = false;
  captchaWidgetId: string = null;
  selectedProducts: number[] = [];
  discountApplied: number = 0;
  /** Resolved from landing query param, draft payload, or single-path inference */
  enrollmentType: EnrollmentType | null = null;

  step: step = 'data';
  error: string = null;
  verifyInfoMessage: string | null = null; // Info message for verify component

  constructor(
    private enrollmentService: EnrollmentService,
    private landingService: LandingPagesService,
    private route: ActivatedRoute,
    private router: Router,
    private turnstileTokenService: TurnstileTokenService
  ) {}

  get isPayrollDeducted(): boolean {
    return this.clientInfo?.billingType === BillingTypes.PayrollDeducted;
  }

  ngOnInit(){
    this.loading = true;
    const id = this.route.snapshot.paramMap.get('id');
    const sessionToken = this.route.snapshot.paramMap.get('sessionToken');
    const otpToken = this.route.snapshot.paramMap.get('otpToken');

    if (sessionToken && otpToken) {
      // Email link with OTP - auto-verify
      this.handleEmailLinkVerification(sessionToken, otpToken);
    } else if (sessionToken) {
      // Draft-based flow without OTP
      this.handleDraftFlow(sessionToken);
    } else if (id) {
      // Original landing page flow
      this.handleLandingPageFlow(id);
    }
  }

  private handleEmailLinkVerification(sessionToken: string, otpToken: string) {
    this.sessionToken = sessionToken;

    // Auto-verify email using the OTP from the link
    this.enrollmentService.verifyEmail({ sessionToken, otpToken }).subscribe({
      next: (response) => {
        if (response.valid && response.data?.isEmailVerified) {
          this.validationToken = response.data.validationToken;
          this.loadDraftAndContinue(sessionToken, response.data.isEnrollmentCompleted);
        } else {
          // Verification failed
          this.handleVerificationError(sessionToken);
        }
      },
      error: () => {
        this.handleVerificationError(sessionToken);
      }
    });
  }

  private loadDraftAndContinue(sessionToken: string, enrollmentCompleted = false) {
    this.enrollmentService.checkDraftStatus(sessionToken).subscribe({
      next: response => {
        this.loading = false;
        if (response.valid && response.data) {
          const draftData = response.data;
          this.applyDraftStatus(draftData);

          if (draftData.enrollmentData?.landingPageId) {
            this.landingService.getLandingPageInfo(draftData.enrollmentData.landingPageId).subscribe({
              next: landingResponse => {
                this.clientInfo = landingResponse.data;
                this.selectedProducts = draftData.enrollmentData?.selectedProducts || [];

                if (this.memberData.paymentFrequency) {
                  this.clientInfo.defaultPaymentFrequency = this.memberData.paymentFrequency;
                }

                this.discountApplied = this.clientInfo.paymentFrequencies
                  .find(f => f.numberOfIntervals === this.memberData.paymentFrequency)?.discount || 0;

                this.applyDraftNextStep(
                  enrollmentCompleted ? 'confirmation' : draftData.nextStep,
                  draftData.emailVerified,
                  draftData.validationToken
                );
                this.router.navigate(['/enrollment', this.sessionToken], { replaceUrl: true });
              }
            });
          } else {
            this.applyDraftNextStep(
              enrollmentCompleted ? 'confirmation' : draftData.nextStep,
              draftData.emailVerified,
              draftData.validationToken
            );
            this.router.navigate(['/enrollment', this.sessionToken], { replaceUrl: true });
          }
        } else {
          this.error = FormErrors.GenericError;
        }
      },
      error: (response) => {
        this.loading = false;
        let error = response?.error?.messages?.[0] as ErrorDto;

        const errorMessage = error?.message?.toLowerCase() || '';
        if (errorMessage.includes('draft not found') ||
            (error?.errorType === 'Validation' && errorMessage.includes('draft'))) {
          this.error = FormErrors.GenericError;
          return;
        }

        if(error?.errorType == ResponseErrors.UserAccess){
          this.step = 'closed';
          return;
        }

        this.error = FormErrors.GenericError;
      }
    });
  }

  private handleVerificationError(sessionToken: string) {
    this.enrollmentService.checkDraftStatus(sessionToken).subscribe({
      next: response => {
        if (!response.valid || !response.data) {
          this.loading = false;
          this.error = FormErrors.GenericError;
          return;
        }

        const draftData = response.data;
        this.applyDraftStatus(draftData);

        const continueAfterLanding = () => {
          this.loading = false;
          if (draftData.emailVerified) {
            this.applyDraftNextStep(draftData.nextStep, true, draftData.validationToken);
            this.router.navigate(['/enrollment', this.sessionToken], { replaceUrl: true });
            return;
          }

          this.verifyInfoMessage = null;
          this.step = 'verify';
          window.history.replaceState({}, '', `/enrollment/${this.sessionToken}`);
        };

        const landingId = draftData.enrollmentData?.landingPageId;
        if (landingId) {
          this.landingService.getLandingPageInfo(landingId).subscribe({
            next: landingResponse => {
              this.clientInfo = landingResponse.data;
              this.selectedProducts = draftData.enrollmentData?.selectedProducts || [];
              if (this.memberData?.paymentFrequency) {
                this.clientInfo.defaultPaymentFrequency = this.memberData.paymentFrequency;
              }
              this.discountApplied = this.clientInfo.paymentFrequencies
                ?.find(f => f.numberOfIntervals === this.memberData.paymentFrequency)?.discount || 0;
              continueAfterLanding();
            },
            error: () => {
              this.loading = false;
              this.error = FormErrors.GenericError;
            }
          });
        } else {
          continueAfterLanding();
        }
      },
      error: (response) => {
        this.loading = false;
        const error = response?.error?.messages?.[0] as ErrorDto;
        if (error?.errorType == ResponseErrors.UserAccess) {
          this.step = 'closed';
          return;
        }
        this.error = FormErrors.GenericError;
      }
    });
  }

  private applyDraftStatus(draftData: CheckDraftStatusResponse) {
    this.memberData = draftData.enrollmentData;
    this.syncEnrollmentTypeFromDraft(draftData.enrollmentData);
    this.maskedEmail = draftData.email;
    this.otpExpiresAt = draftData.otpExpiresAt ?? null;
    this.isEmailVerified = draftData.emailVerified;
  }

  private handleDraftFlow(sessionToken: string) {
    this.sessionToken = sessionToken;
    this.enrollmentService.checkDraftStatus(sessionToken).subscribe({
      next: response => {
        this.loading = false;
        if (response.valid && response.data) {
          const draftData = response.data;
          this.applyDraftStatus(draftData);

          // We need to get the client info from the landing page
          if (draftData.enrollmentData?.landingPageId) {
            this.landingService.getLandingPageInfo(draftData.enrollmentData.landingPageId).subscribe({
              next: landingResponse => {
                this.clientInfo = landingResponse.data;
                this.selectedProducts = draftData.enrollmentData?.selectedProducts || [];

                // Use the user's selected payment frequency if available
                if (this.memberData.paymentFrequency) {
                  this.clientInfo.defaultPaymentFrequency = this.memberData.paymentFrequency;
                }

                this.discountApplied = this.clientInfo.paymentFrequencies
                  .find(f => f.numberOfIntervals === this.memberData.paymentFrequency)?.discount || 0;

                this.applyDraftNextStep(draftData.nextStep, draftData.emailVerified, draftData.validationToken);
              }
            });
          } else {
            this.applyDraftNextStep(draftData.nextStep, draftData.emailVerified, draftData.validationToken);
          }
        } else {
          this.error = FormErrors.GenericError;
        }
      },
      error: (response) => {
        this.loading = false;
        let error = response?.error?.messages?.[0] as ErrorDto;

        if(error?.errorType == ResponseErrors.UserAccess){
          this.step = 'closed';
          return;
        }

        this.error = FormErrors.GenericError;
      }
    });
  }

  private handleLandingPageFlow(id: string) {
      this.landingService.getLandingPageInfo(id).subscribe({
        next: response => {
          this.loading = false;
          this.clientInfo = response.data;

          const params = this.route.snapshot.queryParamMap;
          const productIds = params.getAll('selected');
          if (!productIds?.length) {
            this.router.navigate(['/continue', id]);
            return;
          }

          this.clientInfo.defaultPaymentFrequency = parseInt(
            params.get('paymentFrequency') ?? String(this.clientInfo.defaultPaymentFrequency ?? 1),
            10
          );
          this.discountApplied =
            this.clientInfo.paymentFrequencies?.find(
              (f) => f.numberOfIntervals === this.clientInfo.defaultPaymentFrequency
            )?.discount ?? 0;

          const resolution = EnrollmentLandingPathRules.resolveEnrollmentTypeForWizard(
            this.clientInfo,
            params.get('enrollmentType'),
            this.clientInfo.products
          );
          if (resolution.redirectToPathPicker) {
            this.router.navigate(['/continue', id], {
              queryParams: {
                selected: productIds,
                paymentFrequency: this.clientInfo.defaultPaymentFrequency,
                enrollmentType: params.get('enrollmentType') || undefined,
              },
            });
            return;
          }
          this.enrollmentType = resolution.enrollmentType;
          this.applySelectedProducts(productIds);
        },
      error: (response) => {
        this.loading = false;
        let error = response?.error?.messages?.[0] as ErrorDto;

        if(error?.errorType == ResponseErrors.UserAccess){
          this.step = 'closed';
          return;
        }

        this.error = FormErrors.GenericError;
      }
    });
  }

  private applySelectedProducts(productIds: string[]): void {
    const catalog = this.clientInfo.products ?? [];
    const iso = EnrollmentLandingPathRules.pathEffectiveDateIso(this.clientInfo, this.enrollmentType);
    const asOf = iso ?? new Date();
    const picked = productIds
      .map(id => catalog.find(p => p.clientProductId === parseInt(id, 10)))
      .filter((p): p is MasterClientProductViewDto => !!p);
    this.selectedProducts = remapSelectionForDate(catalog, picked, asOf)
      .map(p => p.clientProductId)
      .filter((id): id is number => id != null);
  }

  memberDataCompleted(data: EnrollmentDraft){
    // Update member data
    this.memberData = data;
    this.memberData.selectedProducts = Array.isArray(this.selectedProducts) ? [...this.selectedProducts] : [];
    this.memberData.landingPageId = this.clientInfo.id;
    this.ensureEnrollmentTypeForLandingDraft();
    if (this.enrollmentType) {
      this.memberData.enrollmentType = this.enrollmentType;
    }

    this.loading = true;
    this.error = null;

    const submitDraft = () => {
      const draftObservable = this.sessionToken
        ? this.enrollmentService.updateDraft(this.sessionToken, data, false, this.isPayrollDeducted)
        : this.enrollmentService.createDraft(data, this.isPayrollDeducted);

      draftObservable.subscribe({
        next: (response) => {
          if (!response?.valid) {
            const msg = response?.messages?.[0]?.message;
            this.error = msg || FormErrors.GenericError;
            this.loading = false;
            return;
          }

          this.draftResponse = response.data;
          this.sessionToken = response.data.sessionToken;
          this.maskedEmail = response.data.email;
          this.otpExpiresAt = response.data.otpExpiresAt;
          this.isEmailVerified = response.data.isEmailVerified;

          if (response.data.isEmailVerified) {
            this.step = this.isPayrollDeducted ? 'confirmation' : 'payment';
          } else {
            this.step = 'verify';
          }

          this.turnstileTokenService.clear();
          this.loading = false;
          this.router.navigate(['/enrollment', this.sessionToken], { replaceUrl: true });
        },
        error: (err) => {
          this.loading = false;
          this.turnstileTokenService.reset();
          const msg = err?.error?.messages?.[0]?.message;
          this.error = msg || FormErrors.GenericError;
        }
      });
    };

    if (this.isPayrollDeducted) {
      this.turnstileTokenService.ensureValidToken()
        .then(() => submitDraft())
        .catch(() => {
          this.loading = false;
          this.turnstileTokenService.reset();
          this.error = FormErrors.TurnstileError;
        });
      return;
    }

    submitDraft();
  }


  get selectedPlans(){
    return this.clientInfo.products.filter(x => this.memberData.selectedProducts.includes(x.clientProductId));
  }

  get confirmationEffectiveDate(): string {
    if (!this.clientInfo) {
      return '';
    }
    const raw = EnrollmentLandingPathRules.pathEffectiveDateIso(this.clientInfo, this.enrollmentType ?? 'OE');
    return EnrollmentLandingPathRules.formatLandingDateForDisplay(raw ?? undefined);
  }

  verifyError(error: string){
    if(error == 'timeout'){
      // stay on verify step - error is shown in the verify component
      this.step = 'verify';
      this.error = null; // Don't show parent error - let child handle it
      this.verifyInfoMessage = null; // Clear info message
    }
    else if(error == 'change-email'){
      try {
        this.turnstileTokenService.reset();
      } catch {
        // The widget is hidden on the verify step, so Cloudflare has nothing to reset.
      }
      this.step = 'data';
      this.error = null;
      this.verifyInfoMessage = null;
      this.turnstileTokenService.requestReset();
    }
    else if(error == 'invalid-code'){
      // stay on verify step - error is shown in the verify component
      this.step = 'verify';
      this.error = null;
      this.verifyInfoMessage = null; // Clear info message when showing error
    }
    else{
      // other errors go back to data step
      this.step = 'data';
      this.error = FormErrors.GenericError;
      this.verifyInfoMessage = null; // Clear info message
    }
    // Note: No need to reset captcha for verify step errors
  }

  emailVerified(event: { validationToken: string; isEnrollmentCompleted?: boolean }){
    this.validationToken = event.validationToken;
    this.step = event.isEnrollmentCompleted || this.isPayrollDeducted ? 'confirmation' : 'payment';
    this.error = null;
    this.verifyInfoMessage = null;
  }

  completeEnrollment(request: CompleteEnrollmentRequest){
    this.loading = true;
    this.error = null;

    this.enrollmentService.completeEnrollment(request).subscribe({
      next: (result) => {
        if(result.valid && result.data){
          this.step = 'confirmation';
        } else {
          this.step = 'payment';
          this.error = FormErrors.GenericError;
        }
        this.loading = false;
      },
      error: (response) => {
        let error = response?.error?.messages?.[0] as ErrorDto;

        if(error?.errorType == ResponseErrors.Payment){
          this.step = 'payment';
          this.error = error.message;
        }
        else if(error?.errorType == ResponseErrors.Application && error.message == ResponseErrors.InvalidToken){
          this.step = 'verify';
          this.error = FormErrors.InvalidToken;
        }
        else{
          this.step = 'payment';
          this.error = FormErrors.GenericError;
        }

        this.loading = false;
      }
    });
  }

  backToVerification(){
    // Preserve the user's selected payment frequency when going back
    if (this.memberData?.paymentFrequency && this.clientInfo) {
      this.clientInfo.defaultPaymentFrequency = this.memberData.paymentFrequency;
      // Recalculate discount based on user's selected frequency
      this.discountApplied = this.clientInfo.paymentFrequencies
        .find(f => f.numberOfIntervals === this.memberData.paymentFrequency)?.discount || 0;
    }
    this.step = 'data';
    this.error = null;
  }

  updateCatpchaId(id: string){
    this.captchaWidgetId = id;
  }

  resetCaptcha(){
      if(this.captchaWidgetId)
        (window as any).turnstile?.reset?.(this.captchaWidgetId);
  }

  private landingPageStatus(): string {
    return (this.clientInfo?.status ?? '').toString().toLowerCase();
  }

  isEnrollmentPeriodInvalid(): boolean {
    if (!this.clientInfo || this.loading) {
      return false;
    }

    if (this.clientInfo.alwaysOpenEnrollment === true) {
      return false;
    }

    if (this.enrollmentType === 'NH') {
      return this.clientInfo.allowNewHireEnrollment !== true;
    }

    if (this.enrollmentType === 'OE') {
      return this.landingPageStatus() !== 'active' || !this.clientInfo.effectiveDate;
    }

    return this.landingPageStatus() !== 'active';
  }

  getEnrollmentPeriodWarningTitle(): string {
    if (!this.clientInfo) {
      return '';
    }

    if (this.enrollmentType === 'NH' && this.clientInfo.allowNewHireEnrollment !== true) {
      return 'New hire enrollment is not available';
    }

    const status = this.landingPageStatus();

    if (status === 'future' && this.clientInfo.startDate != null) {
      const startDate = new Date(this.clientInfo.startDate);
      return 'Enrollment Starts on ' + startDate.toLocaleDateString();
    }

    if (status === 'expired') {
      return 'Enrollment Expired';
    }

    return '';
  }

  getEnrollmentPeriodWarningMessage(): string {
    if (!this.clientInfo) {
      return '';
    }

    if (this.enrollmentType === 'NH' && this.clientInfo.allowNewHireEnrollment !== true) {
      return 'This enrollment link does not allow new hire sign-up. Use open enrollment when it is active, or contact your employer.';
    }

    const now = new Date();

    if (this.clientInfo.startDate) {
      const startDate = new Date(this.clientInfo.startDate);
      if (startDate > now) {
        return `The enrollment period will start on ${startDate.toLocaleDateString()}.`;
      }
    }

    if (this.clientInfo.endDate) {
      const endDate = new Date(this.clientInfo.endDate);
      if (endDate < now) {
        return `The enrollment period ended on ${endDate.toLocaleDateString()}.`;
      }
    }

    return '';
  }

  private applyDraftNextStep(
    nextStep?: string | null,
    emailVerified?: boolean,
    validationToken?: string | null
  ): void {
    this.validationToken = validationToken;
    if (nextStep === 'confirmation' || (emailVerified && this.isPayrollDeducted)) {
      this.step = 'confirmation';
      return;
    }
    if (nextStep === 'complete-payment' || emailVerified) {
      this.step = 'payment';
      return;
    }
    this.step = 'verify';
  }

  private syncEnrollmentTypeFromDraft(draft: EnrollmentDraft | null | undefined): void {
    const t = draft?.enrollmentType;
    if (t === 'OE' || t === 'NH') {
      this.enrollmentType = t;
    }
  }

  private ensureEnrollmentTypeForLandingDraft(): void {
    if (!this.clientInfo?.id) {
      return;
    }
    if (this.enrollmentType === 'OE' || this.enrollmentType === 'NH') {
      return;
    }
    const inferred = EnrollmentLandingPathRules.inferSinglePathEnrollmentType(
      this.clientInfo,
      this.clientInfo.products
    );
    if (inferred) {
      this.enrollmentType = inferred;
    }
  }
}
